import React, { Suspense, lazy } from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Route, Switch } from 'react-router-dom';
import PrivateRoute from '../components/PrivateRoute';
import StaffRoute from '../components/StaffRoute';
import Redirection from '../components/Redirection';
import OrganizationEngagementPage from '../pages/Organization/OrganizationEngagementPage';

const websiteBaseUrl = process.env.REACT_APP_WEBSITE_BASE_URL;

export const OrsRoutes = Object.freeze({
  Root: '/',
  RootTimedOut: '/timeout/:timedout',
  UserManual: '/user-manual',
  CreateReview: '/create-item/:type',
  EditReview: '/:type/:reviewId/edit',
  CreateIR: '/create-item/:type',
  EditIR: '/:type/:reviewId/edit',
  AddComments: '/review/:reviewId/manage-comments/organization/:organizationId/author/:authorId/:isStaff',
  AddCommentsReviewer: '/review/:reviewId/select-comments-reviewer/:origin',
  SharedComments: '/comments/shared/:reviewId/:organizationId/:authorId',
  AddResponses: '/review/:reviewId/manage-responses/proponent/:proponentId',
  AddResponseProponent: '/review/:reviewId/select-response-proponent/:origin',
  SharedResponses: '/responses/shared/:reviewId/:proponentId',
  Register: '/register',
  ResetPassword: '/reset-password',
  NotFound: '/not-found',
  Unauthorized: '/unauthorized',
  EditUserProfile: '/edit-user-profile',
  StaffLogin: '/staff/login',
  StaffPage: '/staff',
  ImportCommentsFile: '/review/:reviewId/upload-comments/:organizationId/:authorId',
  ImportResponsesFile: '/review/:reviewId/upload-responses/:proponentId',
  AddDistributionList: '/system-management/distribution-lists/create',
  EditDistributionList: '/system-management/distribution-lists/edit/:listId',
  SystemManagement: '/system-management/:menuItem',
  ReviewTopics: '/system-management/review-topics',
  AddReviewTopic: '/system-management/review-topics/create',
  EditReviewTopic: '/system-management/review-topics/:reviewTopicId',
  Organizations: '/system-management/organizations',
  Engagement: '/system-management/engagement',
  AdminOrganizations: '/system-management/admin',
  AddEngagementOrg: '/system-managemnt/add-engagement',
  EditOrganization: '/system-management/organizations/:organizationId',
  EditAdminOrganization: '/system-management/organizations-admin/:organizationId',
  AddOrganization: '/system-management/organizations/create',
  EditOrganizationUsers: '/system-management/organizations/:organizationId/users',
  Users: '/system-management/users',
  AddUser: '/system-management/users/create',
  EditUser: '/system-management/users/:userId',
  AnalyticsDashboard: '/analytics-dashboard',
  PasswordSet: '/set-password',
  RedistributeReview: '/review/:reviewId/redistribute',
  FileUpload: '/file-upload',
  Redirection: '/review/:reviewId/redirect',
  NewsLetterSignUp: '/newsletter-signup',
});

export const WebSiteRoutes = {
  Home: `${websiteBaseUrl}`,
  Reviews: `${websiteBaseUrl}/reviews`,
  ReviewDetails: `${websiteBaseUrl}/review/:reviewId`,
};

const ReviewPage = lazy(() => import('../pages/ReviewPage'));
const AddCommentsForm = lazy(() => import('../pages/AddComments'));
const AddCommentsReviewerForm = lazy(() => import('../pages/AddCommentsReviewer'));
const AddResponsesForm = lazy(() => import('../pages/AddResponses'));
const AddResponsesProponentForm = lazy(() => import('../pages/AddResponseProponent'));
const Login = lazy(() => import('../pages/Login'));
const Register = lazy(() => import('../pages/User/Registration/RegistrationPage'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const NotFound = lazy(() => import('../pages/404'));
const Unauthorized = lazy(() => import('../pages/403'));
const StaffLogin = lazy(() => import('../pages/StaffLogin'));
const StaffPage = lazy(() => import('../pages/StaffPage'));
const ImportCommentsFile = lazy(() => import('../pages/ImportCommentsFile'));
const ImportResponsesFile = lazy(() => import('../pages/ImportResponsesFile'));
const OrganizationPage = lazy(() => import('../pages/Organization/OrganizationPage'));
const OrganizationAdminPage = lazy(() => import('../pages/Organization/OrganizationAdminPage'));
const UserProfilePage = lazy(() => import('../pages/User/UserProfile/UserProfilePage'));
const SystemManagement = lazy(() => import('../pages/SystemManagement/SystemManagement'));
const DistributionListForm = lazy(() =>
  import('../pages/SystemManagement/DistributionList/DistributionListForm/DistributionListForm'),
);
const ManageReviewTopicsPage = lazy(() => import('../pages/ReviewTopics/ManageReviewTopicsPage'));
const EditOrganizationUsers = lazy(() => import('../pages/Organization/EditOrganizationUsers'));
const SetPasswordPage = lazy(() => import('../pages/SetPasswordPage'));
const RedistributeReviewForm = lazy(() => import('../pages/RedistributeReview/RedistributeReview'));
const FileUploadPage = lazy(() => import('../pages/FileUpload/FileUpload'));
const AnalyticsDashboard = lazy(() => import('../pages/AnalyticsDashboard/AnalyticsDashboard'));
const NewsLettersignUp = lazy(() => import('../pages/NewsLetterSignUp/NewsLetterSignUp'));
/**
 * All available routes.
 */
const AllRoutes = () => (
  <Switch>
    <Route exact path={[OrsRoutes.Root, OrsRoutes.RootTimedOut]} component={Login} />
    <Route exact path={OrsRoutes.Register} component={Register} />
    <Route exact path={OrsRoutes.ResetPassword} component={ResetPassword} />
    <Route exact path={OrsRoutes.StaffLogin} component={StaffLogin} />
    <Route exact path={OrsRoutes.AnalyticsDashboard} component={AnalyticsDashboard} />
    <Route exact path={OrsRoutes.NewsLetterSignUp} component={NewsLettersignUp} />
    <Redirection exact path={OrsRoutes.Redirection} component={Login} url={websiteBaseUrl} />
    <PrivateRoute exact path={OrsRoutes.CreateReview} component={ReviewPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.EditReview} component={ReviewPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.CreateIR} component={ReviewPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.EditIR} component={ReviewPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.AddOrganization} component={OrganizationPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.EditOrganizationUsers} component={EditOrganizationUsers} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.EditOrganization} component={OrganizationPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.EditAdminOrganization} component={OrganizationAdminPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.AddEngagementOrg} component={OrganizationEngagementPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.AddUser} component={UserProfilePage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.EditUser} component={UserProfilePage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.SystemManagement} component={SystemManagement} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.AddReviewTopic} component={ManageReviewTopicsPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.EditReviewTopic} component={ManageReviewTopicsPage} staffOnly={true} />
    <PrivateRoute exact path={OrsRoutes.AddDistributionList} component={DistributionListForm} />
    <PrivateRoute exact path={OrsRoutes.EditDistributionList} component={DistributionListForm} />
    <PrivateRoute exact path={OrsRoutes.AddComments} component={AddCommentsForm} />
    <PrivateRoute exact path={OrsRoutes.SharedComments} component={AddCommentsForm} />
    <PrivateRoute exact path={OrsRoutes.AddCommentsReviewer} component={AddCommentsReviewerForm} />
    <PrivateRoute exact path={OrsRoutes.AddResponses} component={AddResponsesForm} />
    <PrivateRoute exact path={OrsRoutes.SharedResponses} component={AddResponsesForm} />
    <PrivateRoute exact path={OrsRoutes.AddResponseProponent} component={AddResponsesProponentForm} />
    <PrivateRoute exact path={OrsRoutes.ImportCommentsFile} component={ImportCommentsFile} />
    <PrivateRoute exact path={OrsRoutes.ImportResponsesFile} component={ImportResponsesFile} />
    <StaffRoute exact path={OrsRoutes.StaffPage} component={StaffPage} />
    <Route exact path={OrsRoutes.Unauthorized} component={Unauthorized} />
    <PrivateRoute exact path={OrsRoutes.EditUserProfile} component={UserProfilePage} />
    <Route exact path={OrsRoutes.PasswordSet} component={SetPasswordPage} />
    <PrivateRoute exact path={OrsRoutes.RedistributeReview} component={RedistributeReviewForm} />
    <Route exact path={OrsRoutes.FileUpload} component={FileUploadPage} />
    <Route component={NotFound} />
  </Switch>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<LinearProgress />}>
      <AllRoutes />
    </Suspense>
  );
};

export { AppRoutes as Routes };
